import React, { useEffect, useRef, useState } from "react";
import { Outlet, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Layout.css";
import logo from "../assets/logo.png";
import LanguageSwitcher from "./LanguageSwitcher";
import api from "../services/api";

const Layout = () => {
    const { t } = useTranslation();

    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    const [serverIp, setServerIp] = useState("127.0.0.1");
    const [serverPort, setServerPort] = useState("5000");
    const [protocol, setProtocol] = useState("http");

    const [uid, setUid] = useState(
        localStorage.getItem("kaniArchive_userId") || "1"
    );

    const settingsRef = useRef(null);

    const buildApiUrl = (ip, port) => {
        return `${protocol}://${ip}:${port}/dev`;
    };

    useEffect(() => {
        const randomIndex = Math.floor(Math.random() * 8);
        const selectedImage = `bg${randomIndex}.jpg`;
        const imageUrl = `/assets/${selectedImage}`;
        document.body.style.setProperty(
            "--random-bg-image",
            `url('${imageUrl}')`
        );

        const savedUrl = api.getApiBaseUrl();
        try {
            const urlObj = new URL(savedUrl, window.location.origin);
            setServerIp(urlObj.hostname || "127.0.0.1");
            setServerPort(urlObj.port || "5000");
            setProtocol((urlObj.protocol || "http:").replace(":", ""));
        } catch (e) {
            console.warn("Can't parse API URL, using default", e);
        }

        const localStorageKey = "kaniArchive_userId";
        const storedUid = localStorage.getItem(localStorageKey);
        if (storedUid && !isNaN(parseInt(storedUid, 10))) {
            setUid(storedUid);
        } else {
            localStorage.setItem(localStorageKey, "1");
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                settingsRef.current &&
                !settingsRef.current.contains(e.target)
            ) {
                setIsSettingsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleUidChange = (event) => {
        const newUid = event.target.value;
        setUid(newUid);
        localStorage.setItem("kaniArchive_userId", newUid);
        console.log("User ID saved:", newUid);
    };

    const handleServerIpChange = (e) => {
        const v = e.target.value;
        setServerIp(v);
        const url = buildApiUrl(v, serverPort);
        api.setApiBaseUrl(url);
        console.log("API Base URL saved:", url);
    };

    const handleServerPortChange = (e) => {
        const v = e.target.value;
        setServerPort(v);
        const url = buildApiUrl(serverIp, v);
        api.setApiBaseUrl(url);
        console.log("API Base URL saved:", url);
    };

    const handleProtocolChange = (e) => {
        const v = e.target.value;
        setProtocol(v);
        const url = `${v}://${serverIp}:${serverPort}/dev`;
        api.setApiBaseUrl(url);
        console.log("API Base URL saved:", url);
    };

    return (
        <>
            <nav className="fixed top-0 left-0 right-0 z-50 topbar-bg shadow-md">
                <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* left */}
                        <div className="flex items-center">
                            <Link to="/" className="flex-shrink-0">
                                <img
                                    className="h-12 w-auto rounded-md"
                                    src={logo}
                                    alt="Logo"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src =
                                            "https://placehold.co/120x48/3b82f6/ffffff?text=Logo&font=inter";
                                    }}
                                />
                            </Link>
                            <div className="hidden md:block">
                                <div className="ml-10 flex items-baseline space-x-4">
                                    <Link
                                        to="/character"
                                        className="hover:bg-gray-200 px-3 py-2 rounded-md topbar-title transition-colors"
                                    >
                                        {t("nav.character")}
                                    </Link>
                                    <Link
                                        to="/arena"
                                        className="hover:bg-gray-200 px-3 py-2 rounded-md topbar-title transition-colors"
                                    >
                                        {t("nav.arena")}
                                    </Link>
                                    <Link
                                        to="/content"
                                        className="hover:bg-gray-200 px-3 py-2 rounded-md topbar-title transition-colors"
                                    >
                                        {t("nav.raid")}
                                    </Link>
                                    <Link
                                        to="/command"
                                        className="hover:bg-gray-200 px-3 py-2 rounded-md topbar-title transition-colors"
                                    >
                                        {t("nav.command")}
                                    </Link>
                                    <Link
                                        to="/settings"
                                        className="hover:bg-gray-200 px-3 py-2 rounded-md topbar-title transition-colors"
                                    >
                                        {t("nav.settings")}
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* right */}
                        <div className="flex items-center space-x-4">
                            <LanguageSwitcher />
                            <input
                                id="uid-input"
                                type="hidden"
                                value={uid}
                                readOnly
                            />

                            <div className="relative" ref={settingsRef}>
                                <button
                                    aria-label="Settings"
                                    aria-expanded={isSettingsOpen}
                                    onClick={() => setIsSettingsOpen((s) => !s)}
                                    className="p-2 rounded-md hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-400"
                                    title={t("nav.settings") || "Settings"}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        className="h-6 w-6 text-gray-700"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="32"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M262.29,192.31a64,64,0,1,0,57.4,57.4A64.13,64.13,0,0,0,262.29,192.31ZM416.39,256a154.34,154.34,0,0,1-1.53,20.79l45.21,35.46A10.81,10.81,0,0,1,462.52,326l-42.77,74a10.81,10.81,0,0,1-13.14,4.59l-44.9-18.08a16.11,16.11,0,0,0-15.17,1.75A164.48,164.48,0,0,1,325,400.8a15.94,15.94,0,0,0-8.82,12.14l-6.73,47.89A11.08,11.08,0,0,1,298.77,470H213.23a11.11,11.11,0,0,1-10.69-8.87l-6.72-47.82a16.07,16.07,0,0,0-9-12.22,155.3,155.3,0,0,1-21.46-12.57,16,16,0,0,0-15.11-1.71l-44.89,18.07a10.81,10.81,0,0,1-13.14-4.58l-42.77-74a10.8,10.8,0,0,1,2.45-13.75l38.21-30a16.05,16.05,0,0,0,6-14.08c-.36-4.17-.58-8.33-.58-12.5s.21-8.27.58-12.35a16,16,0,0,0-6.07-13.94l-38.19-30A10.81,10.81,0,0,1,49.48,186l42.77-74a10.81,10.81,0,0,1,13.14-4.59l44.9,18.08a16.11,16.11,0,0,0,15.17-1.75A164.48,164.48,0,0,1,187,111.2a15.94,15.94,0,0,0,8.82-12.14l6.73-47.89A11.08,11.08,0,0,1,213.23,42h85.54a11.11,11.11,0,0,1,10.69,8.87l6.72,47.82a16.07,16.07,0,0,0,9,12.22,155.3,155.3,0,0,1,21.46,12.57,16,16,0,0,0,15.11,1.71l44.89-18.07a10.81,10.81,0,0,1,13.14,4.58l42.77,74a10.8,10.8,0,0,1-2.45,13.75l-38.21,30a16.05,16.05,0,0,0-6.05,14.08C416.17,247.67,416.39,251.83,416.39,256Z" />
                                    </svg>
                                </button>

                                {isSettingsOpen && (
                                    <div className="absolute right-0 mt-2 w-72 bg-white/90 backdrop-filter backdrop-blur-sm rounded-md border border-gray-200 shadow-lg p-3 z-50">
                                        {/* UID */}
                                        <label className="block text-sm font-medium text-slate-700 mt-3">
                                            {t("nav.uid") || "UID"}
                                            <input
                                                type="text"
                                                value={uid}
                                                onChange={handleUidChange}
                                                className="mt-1 w-full bg-white/80 text-slate-800 border border-gray-300 rounded-md px-2 py-1 text-sm"
                                            />
                                        </label>
                                        {/* Protocol / IP / Port */}
                                        <div className="grid grid-cols-3 gap-2 mt-2">
                                            <label className="block text-xs text-slate-600">
                                                {t("nav.protocol") || "Protocol"}
                                                <select
                                                    value={protocol}
                                                    onChange={handleProtocolChange}
                                                    className="mt-1 w-full bg-white/80 text-slate-800 border border-gray-300 rounded-md px-2 py-1 text-sm"
                                                >
                                                    <option value="http">{t("nav.http") || "http"}</option>
                                                    <option value="https">{t("nav.https") || "https"}</option>
                                                </select>
                                            </label>
                                            <label className="block text-xs text-slate-600">
                                                IP
                                                <input
                                                    type="text"
                                                    value={serverIp}
                                                    onChange={
                                                        handleServerIpChange
                                                    }
                                                    className="mt-1 w-full bg-white/80 text-slate-800 border border-gray-300 rounded-md px-2 py-1 text-sm"
                                                    placeholder="127.0.0.1"
                                                />
                                            </label>
                                            <label className="block text-xs text-slate-600">
                                                Port
                                                <input
                                                    type="text"
                                                    value={serverPort}
                                                    onChange={
                                                        handleServerPortChange
                                                    }
                                                    className="mt-1 w-full bg-white/80 text-slate-800 border border-gray-300 rounded-md px-2 py-1 text-sm"
                                                    placeholder="5000"
                                                />
                                            </label>
                                        </div>

                                        <p className="mt-2 text-xs text-gray-500 break-all">
                                            Current API URL: {buildApiUrl(serverIp, serverPort)}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            <main className="container mx-auto max-w-7xl pt-20 md:pt-24 p-4 md:p-8">
                <Outlet />
            </main>
        </>
    );
};

export default Layout;
